<script setup>
import { useWebStore } from "~/store/web";
import { useRoute } from "vue-router";
import { useScrollToComponent } from "~/composables/useScrollToComponent";


const store = useWebStore();
const route = useRoute();
const { scrollToComponent } = useScrollToComponent();


// LANGUAGE SETTING
route.path.split('/')[1] !== store.chosenLang && store.setLanguage(route.path.split('/')[1]);
// CURRENT TAB SETTING
store.chosenTab = store.sites[3].url;

const showAllProducts = ref(false);

const buttonText = computed(() => showAllProducts.value ? store.locale.current.bbmp4 : store.locale.current.bbmp3);

const products = store.getProducts({ category: 'blowingMachine' });
const productDetails = store.getProductPropertyCategories({ type: 'blowingMachine' });
const visibleProducts = computed(() => showAllProducts.value ? products : products.slice(0, 6));

function toggleShowAllProducts() {
  showAllProducts.value = !showAllProducts.value;
}

</script>
<template>
  <div class="blowingMachineMainView">
    <banner-carousel
        :slides="[
            {title:store.getLocaleString('bbmpb1'), subtitle: store.getLocaleString('bbmpb2'), image:'pa-2-eco.webp'},
            {title:store.getLocaleString('bbmpb3'), subtitle: store.getLocaleString('bbmpb4'), image:'pe-uni.webp'}
        ]"
        :card-width="'500px'"
    />
    <areas-carousel
        class=""
        :slides="[
            {title:store.getLocaleString('bbmpc1'), subtitle: store.getLocaleString('bbmpc2'), image:'Tejuzemek.webp'},
            {title:store.getLocaleString('bbmpc3'), subtitle: store.getLocaleString('bbmpc4'), image:'Sorfozdek-boraszatok.webp'},
            {title:store.getLocaleString('bbmpc5'), subtitle: store.getLocaleString('bbmpc6'), image:'Uditogyartas.webp'},
            {title:store.getLocaleString('bbmpc7'), subtitle: store.getLocaleString('bbmpc8'), image:'Kozmetikai-cegek.webp'},
            {title:store.getLocaleString('bbmpc9'), subtitle: store.getLocaleString('bbmpc10'), image:'Olajuzemek.webp'},
        ]"
        :items-to-show="store.getAreaBannerSlidesToShow()"
    />
    <enquiry-button @click="scrollToComponent('enquiry')"/>
    <h1 class="InterExtraBold text-3xl mt-8 md:text-4xl lg:text-5xl">{{ store.sites[3].text }}</h1>
    <title-text-image-template
        :class="store.screenSize === 'mobile'?'m-top-40':'m-top-80 m-bot-80'"
        :annotation="store.getLocaleString('bbmptti1')"
        :title="store.getLocaleString('bbmptti2')"
        :text0="store.getLocaleString('bbmptti3')"
        :text1="store.getLocaleString('bbmptti4')"
        image="sample_bottleblowing_machine.webp"
    />

    <div class="p-5">
      <catalog-button :text="store.getLocaleString('h19')" :file="store.getLocaleString('l20')"/>
    </div>

    <div class="animatedText"><p>{{store.getLocaleString('at')}}</p></div>

    <h3 class="ls-03 text-xl InterExtraBold m0 p-top-80 lg:text-3xl xl:text-4xl">{{store.getLocaleString('bbmp1')}}</h3>

    <product-hero
        logo="PE-2_UNI.webp"
        image="poliol_plusz.webp"
        :slogan="store.getLocaleString('bbmppc11')"
        :text0="store.getLocaleString('bbmppc12')"
        :text1="store.getLocaleString('bbmppc13')"
        :list-items="store.getLocaleString('bbmppc14')"
        :dark="false"
    />

    <product-hero
        logo="PA-2_ECO+_LOGO.webp"
        image="PA-2_ECO+_fekvo_renderelt.webp"
        class="bg-black"
        :slogan="store.getLocaleString('bbmppc21')"
        :text0="store.getLocaleString('bbmppc22')"
        :text1="store.getLocaleString('bbmppc23')"
        :list-items="store.getLocaleString('bbmppc24')"
        :dark="true"
    />


    <h3 class="ls-03 title InterExtraBold p-top-80">{{store.getLocaleString('bbmp2')}}</h3>
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 xl:grid-cols-3 xl:w-5/6 xl:mx-auto xl:gap-10">
      <div v-for="(prod, index) in visibleProducts" :key="index" class="w-full aspect-[3/4]">
        <flip-card v-if="index < 6 || showAllProducts">
          <template #front-content>
            <Product 
              :name="prod.name" 
              :details="productDetails" 
              :values="prod.values" 
              :detailed="true" 
              :image="prod.image"
            />
          </template>
          <template #back-content>
            <Product 
              :name="prod.name" 
              :details="productDetails" 
              :values="prod.values" 
              :detailed="true" 
              :overlay="true" 
              :overlaytext="prod.overlayText" 
              :image="prod.image" 
              @enquiryClicked="scrollToComponent('enquiry')"
            />
          </template>
        </flip-card>
      </div>
    </div>
    <EnquiryButton :text="buttonText" class="m-bot-80" :black="true" @click="toggleShowAllProducts"/>

    <div class="animatedText" :class="store.screenSize === 'mobile'?'p-top-40':''"><p>{{store.getLocaleString('at')}}</p></div>

    <ContactUsPage class="pTop80" title="Keressen minket!"
                   subtitle="Forduljon hozzánk bizalommal"
                   contact-phone="06 20 485 0585"
                   contact-topic="blowingMachine"
    />

    <CustomCarousel :no-of-slides="2.5" bordered="false" :slides="['medikemia-logo.webp','szentkiralyi_logo.webp','fantana-mare_logo.webp','resilux_logo.webp','royalsekt_logo.webp','florin_logo.webp','caola_logo.webp']" />
    <footer-comp/>
  </div>
</template>

<style scoped lang="scss">
  .blowingMachineMainView{
    padding: unset;
    width: 100%;
    .animatedText {
      //margin-top: ;
      overflow: hidden;
      width: 100%;
      white-space: nowrap;
      font-size: 40px;
      font-family: Space-Grotesk, sans-serif;
      font-weight: 500;
      color: rgb(255, 118, 24);

      p {
        margin: 0;
        margin-bottom: .5%;
        -webkit-font-smoothing: antialiased;
        letter-spacing: -2px;
      }
    }

  }
</style>